/* eslint-disable no-restricted-imports */
/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';

import type { AudiVideoServiceInterfaceV1 } from '@oneaudi/video-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import {
  ContentContextProvider,
  FootnoteContextProvider,
  fetchCaeEnforcement,
} from '@oneaudi/feature-app-utils';
import type { GfaServiceConfigLike, AcsResult } from '@oneaudi/feature-app-utils';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { NumberFormatterService } from '@oneaudi/number-formatter-service';
import App from './FeatureApp';
import ContextProvider from './Context';
import { isTrackingDisabled } from './utils/debugging';
import { getDesignLibrary } from './utils/alphaHelper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'audi-video-service': AudiVideoServiceInterfaceV1 | undefined;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1 | undefined;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
  readonly 'onegraph-service': OneGraphServiceV1;
  readonly 'audi-number-formatter-service': NumberFormatterService;
  readonly 'gfa:service-config-provider'?: GfaServiceConfigLike;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'locale-service': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'audi-number-formatter-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-current-carline-service': '^1.0.0',
      'audi-video-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
  },

  create: ({
    featureServices,
    featureAppId,
    featureAppName,
  }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const contentService = featureServices['audi-content-service'];
    const renderModeService = featureServices['audi-render-mode-service'];
    const videoService = featureServices['audi-video-service'];
    const localeService = featureServices['locale-service'];
    const i18nService = featureServices['dbad:audi-i18n-service'];
    const footnoteReferenceServiceScopeManager = featureServices['audi-footnote-reference-service'];
    const footnoteReferenceService =
      footnoteReferenceServiceScopeManager?.getDefaultScopeRefService();
    const oneGraphService = featureServices['onegraph-service'];
    const numberFormatterService = featureServices['audi-number-formatter-service'];

    const asyncSsrManager = featureServices['s2:async-ssr-manager'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    const gfaServiceConfigProvider = featureServices['gfa:service-config-provider'];

    const { OneGraphProvider } = oneGraphService;
    const trackingService = !isTrackingDisabled()
      ? (featureServices['audi-tracking-service'] as TrackingServiceV2)
      : undefined;

    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    // CAE enforcement: fetch ACS market config during SSR so we know before first
    // render whether WLTP data is legally required. The result is serialized and
    // reused on the client via useCaeEnforcement (no duplicate fetch).
    const rawContent = contentService.getContent() as Record<string, unknown> | undefined;
    const consumptionAndEmissions: string[] = (
      ((rawContent?.consumptionAndEmissions ??
        (rawContent?.fields as Record<string, unknown> | undefined)?.consumptionAndEmissions) as
        | string[]
        | undefined) ?? []
    ).filter(Boolean);

    let acsResult: AcsResult = { caeDataRequired: false };

    if (asyncSsrManager) {
      asyncSsrManager.scheduleRerender(
        (async () => {
          if (consumptionAndEmissions.length > 0 && gfaServiceConfigProvider) {
            try {
              acsResult = await fetchCaeEnforcement({
                gfaServiceConfigProvider,
              });
            } catch (error) {
              loggerService?.warn(
                'ACS fetch failed during SSR, defaulting to no enforcement',
                error,
              );
              acsResult = { caeDataRequired: false };
            }
          } else {
            acsResult = { caeDataRequired: false };
          }

          serializedStateManager?.register(() => JSON.stringify({ acsResult }));
        })(),
      );
    } else {
      const serializedState = serializedStateManager?.getSerializedState();
      if (serializedState) {
        try {
          const parsed = JSON.parse(serializedState) as { acsResult?: AcsResult };
          acsResult = parsed.acsResult ?? { caeDataRequired: false };
        } catch (error) {
          loggerService?.warn(
            'Failed to parse serialized CAE state, defaulting to no enforcement',
            error,
          );
          acsResult = { caeDataRequired: false };
        }
      }
    }

    return {
      render: () => {
        return (
          <ContentContextProvider contentService={contentService}>
            <OneGraphProvider featureServices={featureServices}>
              <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                <ContextProvider
                  featureAppEnv={{ featureAppId, featureAppName }}
                  localeService={localeService}
                  loggerService={loggerService}
                  trackingService={trackingService}
                  videoService={videoService}
                  oneGraphService={oneGraphService}
                  numberFormatterService={numberFormatterService}
                  i18nService={i18nService}
                  gfaServiceConfigProvider={gfaServiceConfigProvider}
                  acsResult={acsResult}
                >
                  <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                    <UniversalEditorProvider
                      contentService={contentService}
                      renderModeService={renderModeService}
                    >
                      <App />
                    </UniversalEditorProvider>
                  </div>
                </ContextProvider>
              </FootnoteContextProvider>
            </OneGraphProvider>
          </ContentContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
